import { isHttpExceptionModel } from "@/lib/openapi";

// Retry configuration for transient sync failures.
export const MAX_SYNC_RETRIES = 2;
export const SYNC_RETRY_DELAYS = [1000, 3000];

/**
 * Whether an error is transient and worth retrying.
 * Domain errors (CONFLICT, NOT_FOUND, etc.) are not retryable — they require user action.
 */
export const isSyncRetryable = (error: unknown): boolean => {
    if (!isHttpExceptionModel(error)) return true;
    const type = String(error.detail[0]?.type);
    return type === "INTERNAL" || type === "CONNECTION_ERROR";
};

export const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Classify a sync error into a user-facing message.
 *
 * `errorType` is the domain error's type (e.g. "CONFLICT") when present, so
 * callers can branch — notably the thread stale-base soft-block, which is a
 * recoverable CONFLICT that should be caught up rather than "refresh the page".
 */
export const classifySyncError = (
    error: unknown
): { details: string; isDomainError: boolean; errorType?: string } => {
    const isDomainError = isHttpExceptionModel(error);
    let details: string;
    let errorType: string | undefined;

    if (isDomainError) {
        errorType = String(error.detail[0].type);
        if (errorType === "CONFLICT") {
            // Prefer the server's own message — the thread stale-base soft-block
            // ("Main changed this since the thread started — catch up / resolve
            // to edit") is far clearer than a generic line. Fall back only when
            // the server provided no message.
            const serverMessage = error.detail[0].message;
            details =
                typeof serverMessage === "string" && serverMessage.trim()
                    ? serverMessage
                    : "Local changes conflict with the server";
        } else if (errorType === "NOT_FOUND") {
            details =
                "The entity you are trying to sync does not exist anymore";
        } else {
            details = `Server error: ${error.detail[0].message}`;
        }
    } else {
        details = "Network error — could not reach the server";
    }

    return { details, isDomainError, errorType };
};
